import React, { useEffect, useRef, memo } from 'react';

/* ── TRADINGVIEW REAL-TIME CHART ──
   Free embed widget from TradingView. Real-time data served by TradingView.
   Zero API cost. Zero maintenance. Live candles, crosshair, zoom, pan.
   
   GEX levels stay in the cards above — this chart provides live price context.
   No npm install needed — loads from TradingView CDN. */

// Map GammaLens tickers to TradingView symbols
const TV_SYMBOLS = {
  SPY: 'AMEX:SPY',
  QQQ: 'NASDAQ:QQQ',
  IWM: 'AMEX:IWM',
  DIA: 'AMEX:DIA',
  XLF: 'AMEX:XLF',
  SMH: 'NASDAQ:SMH',
  NVDA: 'NASDAQ:NVDA',
  AMD: 'NASDAQ:AMD',
  AVGO: 'NASDAQ:AVGO',
  SMCI: 'NASDAQ:SMCI',
  MU: 'NASDAQ:MU',
  ARM: 'NASDAQ:ARM',
  AAPL: 'NASDAQ:AAPL',
  MSFT: 'NASDAQ:MSFT',
  GOOGL: 'NASDAQ:GOOGL',
  AMZN: 'NASDAQ:AMZN',
  META: 'NASDAQ:META',
  NFLX: 'NASDAQ:NFLX',
  CRM: 'NYSE:CRM',
  ORCL: 'NYSE:ORCL',
  TSLA: 'NASDAQ:TSLA',
  COST: 'NASDAQ:COST',
  WMT: 'NYSE:WMT',
  UNH: 'NYSE:UNH',
  LLY: 'NYSE:LLY',
  XOM: 'NYSE:XOM',
  JPM: 'NYSE:JPM',
  GS: 'NYSE:GS',
  BAC: 'NYSE:BAC',
};

function PriceChart({ ticker }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear previous widget
    containerRef.current.innerHTML = '';

    const tvSymbol = TV_SYMBOLS[ticker] || `NASDAQ:${ticker}`;

    // Create widget container
    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container';
    widgetDiv.style.height = '100%';
    widgetDiv.style.width = '100%';

    const innerDiv = document.createElement('div');
    innerDiv.className = 'tradingview-widget-container__widget';
    innerDiv.style.height = '100%';
    innerDiv.style.width = '100%';
    widgetDiv.appendChild(innerDiv);

    // Create and inject script
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: tvSymbol,
      interval: 'D',
      timezone: 'America/New_York',
      theme: 'dark',
      style: '1', // Candlestick
      locale: 'en',
      backgroundColor: 'rgba(10, 15, 30, 1)',
      gridColor: 'rgba(255, 255, 255, 0.03)',
      hide_top_toolbar: false,
      hide_legend: false,
      allow_symbol_change: false,
      save_image: false,
      calendar: false,
      hide_volume: false,
      support_host: 'https://www.tradingview.com',
      withdateranges: true,
      range: '1M',
      details: false,
      hotlist: false,
      show_popup_button: false,
      enable_publishing: false,
      container_id: 'tradingview_chart',
    });

    widgetDiv.appendChild(script);
    containerRef.current.appendChild(widgetDiv);

    // Force resize so TradingView fills the full container width
    const resizeTimer = setTimeout(() => window.dispatchEvent(new Event('resize')), 300);

    return () => {
      clearTimeout(resizeTimer);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [ticker]);

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '10px',
      }}>
        <span style={{ fontSize: '13px', color: '#94a3b8', letterSpacing: '1.5px', fontWeight: '700', fontFamily: 'monospace' }}>
          {ticker} · LIVE CHART
        </span>
        <span style={{ fontSize: '11px', color: '#475569' }}>
          Powered by TradingView
        </span>
      </div>

      {/* Chart */}
      <div
        ref={containerRef}
        style={{
          height: '420px',
          borderRadius: '8px',
          overflow: 'hidden',
          background: '#0a0f1e',
          width: '100%',
        }}
      />
    </div>
  );
}

export default memo(PriceChart);

const containerStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '20px',
  padding: '18px',
};
